import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback } from 'react'
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import { Controller, useForm } from 'react-hook-form';
import { Offcanvas } from 'react-bootstrap';
import clsx from 'clsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateRateSchema } from '../schema/UpdateRateSchema';
import CurrencyInput from '../../../shared/components/form/CurrencyInput';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import FormError from '../../../shared/components/errors/FormError';
import { applyServerValidationErrors } from '../../../shared/utils/serverValidation';

interface UpdateRateDialogProps {
    show: boolean,
    onClose: () => void,
    rateId: string
}

interface UpdateRateForm {
    name: string,
    description: string,
    max_night: number,
    min_night: number,
    guest_include: number,
    guest_max: number,
    extra_adult: number,
    extra_child: number,
    extra_adult_rate: number,
    extra_child_rate: number,
    min_rate: number,
    rate: number,
}

const UpdateRateDialog: React.FC<UpdateRateDialogProps> = ({ show, onClose, rateId }) => {
    const { data: rate, isFetching, isError } = useQuery({
        queryKey: ['rates', rateId],
        queryFn: () => privateApi.get(`/v4/rate-plan/${rateId}`).then((res) => res.data),
        enabled: !!rateId,
    });

    if (isError) {
        toast.error(rate.error?.response?.data?.message || 'An error occurred', { id: 'update-rate' });
    }

    const { register, handleSubmit, formState: { errors }, reset, control, setError } = useForm<UpdateRateForm>({
        resolver: zodResolver(UpdateRateSchema),
        disabled: !!rate?.isError || !!rate?.isLoading,
        values: {
            name: rate?.data?.name || '',
            description: rate?.data?.description || '',
            max_night: rate?.data?.max_night || 0,
            min_night: rate?.data?.min_night || 0,
            guest_include: rate?.data?.guest_include || 0,
            guest_max: rate?.data?.guest_max || 0,
            extra_adult: rate?.data?.extra_adult || 0,
            extra_child: rate?.data?.extra_child || 0,
            extra_adult_rate: rate?.data?.extra_adult_rate || 0,
            extra_child_rate: rate?.data?.extra_child_rate || 0,
            min_rate: rate?.data?.min_rate || 0,
            rate: rate?.data?.rate || 0,
        },
    })

    const handleClose = () => {
        reset();
        onClose();
    }

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (data: UpdateRateForm) => privateApi.put(`/v4/rate-plan/${rateId}`, data),
        onSuccess: async () => {
            handleClose();

            await queryClient.refetchQueries({
                predicate: (query) => query.queryKey.some((key) => ['rates', 'rooms'].includes(key as string))
            });

            toast.success('Rate updated successfully', { id: 'update-rate' });
        },
        onError: (error: any) => {
            const handled = applyServerValidationErrors(error, setError, {
                toastId: 'update-rate',
            });

            if (!handled) {
                toast.error(error?.response?.data?.message || 'An error occurred', { id: 'update-rate' });
            }
        },
    });

    const onSubmit = useCallback((data: UpdateRateForm) => {
        toast.loading('Updating rate...', { id: 'update-rate' });
        mutate(data);
    }, [mutate]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Update Rate</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {isFetching && <FormSpinner />}
                {isError && <FormError />}
                {rate?.success && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                        {/* Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Rate Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                    id="name"
                                    placeholder='Enter rate name'
                                    {...register('name', { required: 'Rate name is required' })}
                                />
                                {errors?.name && (
                                    <div className="invalid-feedback">{typeof errors.name.message === 'string' && errors.name.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="description" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Description: </label>
                            <div className="col-sm-8">
                                <textarea
                                    className={clsx("form-control app-form-control rounded-1", errors?.description && 'is-invalid')}
                                    id="description"
                                    placeholder='Enter rate description'
                                    {...register('description')}
                                />
                                {errors?.description && (
                                    <div className="invalid-feedback">{typeof errors.description.message === 'string' && errors.description.message}</div>
                                )}
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Rate Settings</h3>
                        {/* Max Night */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="max_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Max Night: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.max_night && 'is-invalid')}
                                    id="max_night"
                                    placeholder='Enter max night'
                                    {...register('max_night', { required: 'Max night is required' })}
                                />
                                {errors?.max_night && (
                                    <div className="invalid-feedback">{typeof errors.max_night.message === 'string' && errors.max_night.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Min Night */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="min_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Min Night: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.min_night && 'is-invalid')}
                                    id="min_night"
                                    placeholder='Enter min night'
                                    {...register('min_night', { required: 'Min night is required' })}
                                />
                                {errors?.min_night && (
                                    <div className="invalid-feedback">{typeof errors.min_night.message === 'string' && errors.min_night.message}</div>
                                )}
                            </div>
                        </div>

                        {/* guest_include */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="guest_include" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Include: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.guest_include && 'is-invalid')}
                                    id="guest_include"
                                    placeholder='Enter guest include'
                                    {...register('guest_include', { required: 'Guest include is required' })}
                                />
                                {errors?.guest_include && (
                                    <div className="invalid-feedback">{typeof errors.guest_include.message === 'string' && errors.guest_include.message}</div>
                                )}
                            </div>
                        </div>

                        {/* guest_max */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="guest_max" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Max: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.guest_max && 'is-invalid')}
                                    id="guest_max"
                                    placeholder='Enter guest max'
                                    {...register('guest_max', { required: 'Guest max is required' })}
                                />
                                {errors?.guest_max && (
                                    <div className="invalid-feedback">{typeof errors.guest_max.message === 'string' && errors.guest_max.message}</div>
                                )}
                            </div>
                        </div>

                        {/* extra_adult */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_adult" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.extra_adult && 'is-invalid')}
                                    id="extra_adult"
                                    placeholder='Enter extra adult'
                                    {...register('extra_adult', { required: 'Extra adult is required' })}
                                />
                                {errors?.extra_adult && (
                                    <div className="invalid-feedback">{typeof errors.extra_adult.message === 'string' && errors.extra_adult.message}</div>
                                )}
                            </div>
                        </div>

                        {/* extra_child */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_child" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.extra_child && 'is-invalid')}
                                    id="extra_child"
                                    placeholder='Enter extra child'
                                    {...register('extra_child', { required: 'Extra child is required' })}
                                />
                                {errors?.extra_child && (
                                    <div className="invalid-feedback">{typeof errors.extra_child.message === 'string' && errors.extra_child.message}</div>
                                )}
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Rate Price Settings</h3>
                        {/* extra_adult_rate */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_adult_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult Rate: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="extra_adult_rate" // Nama field yang diatur oleh React Hook Form
                                    control={control}
                                    rules={{
                                        required: "Extra adult rate is required", // Validasi required
                                        min: {
                                            value: 1000,
                                            message: "Extra adult rate must be at least Rp. 1,000",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field} // Menghubungkan props ke CurrencyInput
                                            value={field.value} // Nilai dari React Hook Form
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }} // Ambil angka murni dari CurrencyInput
                                            error={typeof errors.extra_adult_rate?.message === 'string' ? errors.extra_adult_rate.message : undefined} // Tampilkan pesan error jika ada
                                            placeholder="Enter extra adult rate in Rupiah"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* extra_child_rate */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_child_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child Rate: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="extra_child_rate" // Nama field yang diatur oleh React Hook Form
                                    control={control}
                                    rules={{
                                        required: "Extra child rate is required", // Validasi required
                                        min: {
                                            value: 1000,
                                            message: "Extra child rate must be at least Rp. 1,000",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field} // Menghubungkan props ke CurrencyInput
                                            value={field.value} // Nilai dari React Hook Form
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }} // Ambil angka murni dari CurrencyInput
                                            error={typeof errors.extra_child_rate?.message === 'string' ? errors.extra_child_rate.message : undefined} // Tampilkan pesan error jika ada
                                            placeholder="Enter extra child rate in Rupiah"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* min_rate */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="min_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Min Rate: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="min_rate" // Nama field yang diatur oleh React Hook Form
                                    control={control}
                                    rules={{
                                        required: "Min rate is required", // Validasi required
                                        min: {
                                            value: 1000,
                                            message: "Min rate must be at least Rp. 1,000",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field} // Menghubungkan props ke CurrencyInput
                                            value={field.value} // Nilai dari React Hook Form
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }} // Ambil angka murni dari CurrencyInput
                                            error={typeof errors.min_rate?.message === 'string' ? errors.min_rate.message : undefined} // Tampilkan pesan error jika ada
                                            placeholder="Enter min rate in Rupiah"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* rate */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Rate: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="rate" // Nama field yang diatur oleh React Hook Form
                                    control={control}
                                    rules={{
                                        required: "Rate is required", // Validasi required
                                        min: {
                                            value: 1000,
                                            message: "Rate must be at least Rp. 1,000",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field} // Menghubungkan props ke CurrencyInput
                                            value={field.value} // Nilai dari React Hook Form
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }} // Ambil angka murni dari CurrencyInput
                                            error={typeof errors.rate?.message === 'string' ? errors.rate.message : undefined} // Tampilkan pesan error jika ada
                                            placeholder="Enter rate in Rupiah"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="form-group mt-auto d-grid">
                            <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                                {isPending ? 'Loading...' : 'Update Rate'}
                            </button>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default UpdateRateDialog